// eslint-disable-next-line no-restricted-imports
import type { CSSObject } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Typography } from '@iheartjane/reefer';
import type { ReeferTheme } from '@iheartjane/reefer';
import type { PropsWithoutRef } from 'react';

import { useTextField } from './text-field-context';
import type { Variant } from './text-input';

interface TextFieldLabelProps {
  disabled?: boolean;
  error?: boolean;
  focused?: boolean;
  shrink?: boolean;
  success?: boolean;
  theme?: ReeferTheme;
  variant?: Variant;
}

const material = (props: TextFieldLabelProps): CSSObject => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  width: '100%',
  display: 'block',
  transformOrigin: 'top left',
  lineHeight: 1,
  top: 0,
  left: 0,
  position: 'absolute',
  fontWeight: 400,
  color: props.focused
    ? props.theme && props.theme.colors.primary.main
    : props.shrink
      ? props.theme?.colors.grays.black
      : props.theme?.colors.grays.mid,
  transform: props.shrink
    ? 'translate(0, 0px) scale(0.875)'
    : 'translate(0, 29px) scale(1)',
  transition:
    'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
  ...(props.error && props.shrink && { color: props.theme?.colors.error }),
  ...(props.success && props.shrink && { color: props.theme?.colors.success }),
  ...(props.disabled && { color: props.theme?.colors.grays.light }),
});

const TextFieldLabel = styled.label<TextFieldLabelProps>((props) => {
  switch (props.variant) {
    case 'material':
      return material(props);

    default:
      return null;
  }
});

export const Label = ({
  children,
  ...props
}: { disabled?: boolean } & PropsWithoutRef<
  JSX.IntrinsicElements['label']
>) => {
  const { shrink, focused, error, success, variant } = useTextField();

  return (
    <TextFieldLabel
      shrink={shrink}
      focused={focused}
      error={error}
      success={success}
      variant={variant}
      {...props}
    >
      <Typography color="text-main" variant="body">
        {children}
      </Typography>
    </TextFieldLabel>
  );
};
